import { useDroppable } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Fragment, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardHeader } from 'reactstrap';
import { AssignmentCard } from './AssignmentCard';
import { calculatePickingIndexes, heatLevelOf, HEAT_EMOJI } from '../../api/pickingIndexes';
import type { AssignedMission } from '../../api/assignments';

export interface OperatorColumnProps {
  operatorUserId: number;
  /**
   * Posizione dell'operatore nella board: guida la scelta del colore-contorno
   * così i primi N operatori mostrati hanno SEMPRE tinte distinte (colorare per
   * `operatorUserId` collideva su id congrui modulo palette). Fallback su
   * `operatorUserId` se assente.
   */
  colorIndex?: number;
  label: string;
  missions: AssignedMission[];
  onCardClick: (cardId: string, e: React.MouseEvent) => void;
  selectedIds: Set<string>;
  /** Mission flashed green for a few seconds right after a drag&drop move. */
  highlightMissionId?: number | null;
  /**
   * Collassata: nel corpo restano solo le missioni "in corso" (active) e
   * "da chiudere" (awaiting_close); i dati di testa restano completi. Le
   * pending/assegnate sono nascoste (contate come "in coda"). L'header resta
   * un drop-target, quindi si può ancora assegnare a un operatore collassato.
   */
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  /**
   * Live drop hint: insertion index in the rendered mission list where the
   * dragged card would land (null = not the drag target). Cards from that index
   * on slide down by `previewShiftPx`, opening a gap that previews the landing
   * spot. Cross-column only — within-column reorder uses dnd-kit's native shift.
   */
  previewIndex?: number | null;
  previewShiftPx?: number;
}

/** Missioni ancora visibili nel corpo quando la card è collassata. */
function inProgressMissions(missions: AssignedMission[]): AssignedMission[] {
  return missions.filter((m) => m.status === 'active' || m.status === 'awaiting_close');
}

export function opCardId(missionId: number): string {
  return `op-mission-${missionId}`;
}

/**
 * Palette di contorni per-operatore: tinte distinte e ben distanziate sulla
 * ruota dei colori, leggibili sia in light che in dark. Volutamente SENZA il
 * verde brand (#3da300) per non confondersi con lo stato "In corso" delle
 * mission card.
 */
export const OPERATOR_COLORS = [
  '#4b9cf5', // blu
  '#0ab39c', // teal
  '#e6a91e', // ambra
  '#f1734f', // corallo
  '#ec4e8e', // rosa
  '#8b5cf6', // viola
  '#4f5bd5', // indaco
  '#b5744a', // rust
  '#29b6c9', // ciano
  '#d4a017', // oro
  '#e0567a', // fragola
  '#7c8cf8', // periwinkle
  '#c14fd0', // magenta
  '#5a8f9e', // acciaio
  '#f28c28', // arancio
  '#9b8cff', // lavanda
] as const;

/** Colore-contorno stabile per operatore (deterministico su `operatorUserId`). */
export function operatorColorOf(operatorUserId: number): string {
  const idx = Math.abs(Math.trunc(operatorUserId)) % OPERATOR_COLORS.length;
  return OPERATOR_COLORS[idx];
}

/**
 * "mario.rossi@x.it" → "MR"; "alice" → "AL"; "Bob Smith" → "BS".
 */
function initialsOf(label: string): string {
  const clean = (label.split('@')[0] || '').trim();
  if (!clean) return '?';
  const parts = clean.split(/[.\s_-]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return clean.slice(0, 2).toUpperCase();
}

/** ICM medio dell'operatore = media degli ICM delle sue missioni (0 se nessuna). */
function averageIcm(missions: AssignedMission[]): number {
  if (missions.length === 0) return 0;
  const sum = missions.reduce((acc, m) => acc + calculatePickingIndexes({
    locations: m.pick_list.distinct_locations,
    totalQtyAssigned: m.qty_assigned_total ?? m.pick_list.qty_total,
  }).icm, 0);
  return Math.round(sum / missions.length);
}

export function OperatorColumn({ operatorUserId, colorIndex, label, missions, onCardClick, selectedIds, highlightMissionId, collapsed, onToggleCollapse, previewIndex, previewShiftPx = 96 }: OperatorColumnProps) {
  const { t } = useTranslation(['assignment', 'picklist']);
  const droppableId = `op-${operatorUserId}`;
  const { setNodeRef, isOver } = useDroppable({ id: droppableId });
  const totalQty = missions.reduce((s, m) => s + (m.qty_assigned_total ?? m.pick_list.qty_total), 0);
  const initials = initialsOf(label);
  const hasMissions = missions.length > 0;
  const icm = averageIcm(missions);
  const level = heatLevelOf(icm);
  const opColor = operatorColorOf(colorIndex ?? operatorUserId);

  // Corpo: da collassata mostra solo le missioni in corso; l'header resta
  // sempre completo. `hiddenCount` = missioni nascoste (in coda) da collassata.
  const bodyMissions = collapsed ? inProgressMissions(missions) : missions;
  const hiddenCount = missions.length - bodyMissions.length;

  // Altezza del varco d'inserimento = altezza reale di una card di QUESTA colonna
  // (misurata dalla prima card) + spaziatura, così il varco combacia con una card
  // sorella e la Card operatore cresce quel tanto che basta a contenerla. Fallback
  // sull'altezza della card trascinata (`previewShiftPx`) quando la colonna è vuota.
  const firstCardRef = useRef<HTMLDivElement | null>(null);
  const [measuredCardH, setMeasuredCardH] = useState(0);
  useLayoutEffect(() => {
    const h = firstCardRef.current?.getBoundingClientRect().height ?? 0;
    if (h > 0 && Math.abs(h - measuredCardH) > 1) setMeasuredCardH(h);
  });
  const GAP_SPACING = 8;
  const gapPx = (measuredCardH > 0 ? measuredCardH : previewShiftPx) + GAP_SPACING;

  const subLine = hasMissions
    ? `${totalQty} ${t('card.totalPackages').toLowerCase()} · ${missions.length === 1
        ? t('operatorColumn.items_one', { count: missions.length })
        : t('operatorColumn.items_other', { count: missions.length })}`
    : t('operatorColumn.free');

  return (
    <Card
      className="mb-0"
      style={{ borderColor: opColor, borderLeftColor: opColor, borderLeftWidth: '4px' }}
    >
      <CardHeader className="py-2 px-2 d-flex align-items-center bg-body">
        <div className="avatar-xs flex-shrink-0">
          <span className="avatar-title rounded fs-11 fw-semibold"
                style={{ backgroundColor: `${opColor}22`, color: opColor }}>
            {initials}
          </span>
        </div>
        <div className="ms-2 flex-grow-1 min-w-0 text-truncate">
          <div className="fw-semibold text-truncate">{label}</div>
          <div className="small text-muted text-truncate d-flex align-items-center gap-1">
            <span className="text-truncate">{subLine}</span>
            {hasMissions && (
              <span className={`badge rounded-pill vp-heat vp-heat-${level} flex-shrink-0`}
                    aria-label={t('operatorColumn.icmAria', { value: icm, level: t(`picklist:heat.levels.${level}`) })}>
                <span aria-hidden="true">{HEAT_EMOJI[level]}</span>
                <span className="fw-semibold ms-1">{icm}</span>
              </span>
            )}
          </div>
        </div>
        <span className={`badge rounded-pill flex-shrink-0 fw-medium ${hasMissions ? 'bg-primary text-white' : 'bg-light text-muted'}`}>
          {missions.length}
        </span>
        {onToggleCollapse && (
          <button type="button"
                  className="btn btn-sm p-0 border-0 bg-transparent text-muted flex-shrink-0 ms-1 d-inline-flex align-items-center"
                  onClick={onToggleCollapse}
                  aria-expanded={!collapsed}
                  aria-label={collapsed ? t('operatorColumn.expand') : t('operatorColumn.collapse')}
                  title={collapsed ? t('operatorColumn.expand') : t('operatorColumn.collapse')}>
            <i className={`bi bi-chevron-${collapsed ? 'down' : 'up'} fs-15`} />
          </button>
        )}
      </CardHeader>
      <div
        ref={setNodeRef}
        role="region"
        aria-label={`${label} (${missions.length})`}
        className={`card-body p-2 ${isOver ? 'bg-primary-subtle' : ''}`}
        style={{ minHeight: 110 }}
      >
        {bodyMissions.length > 0 ? (
          <>
            <SortableContext items={bodyMissions.map((m) => opCardId(m.mission_id))}
                             strategy={verticalListSortingStrategy}>
              {bodyMissions.map((m, i) => (
                <Fragment key={opCardId(m.mission_id)}>
                  {/* Slot d'inserimento reale: occupa spazio nel flusso, così il box
                      operatore cresce e le card sottostanti restano dentro il bordo. */}
                  {previewIndex === i && <div className="vp-drop-slot" style={{ height: gapPx }} aria-hidden />}
                  <SortableOpCard mission={m}
                                  innerRef={i === 0 ? firstCardRef : undefined}
                                  isSelected={selectedIds.has(opCardId(m.mission_id))}
                                  highlight={m.mission_id === highlightMissionId}
                                  onClick={(e) => onCardClick(opCardId(m.mission_id), e)} />
                </Fragment>
              ))}
            </SortableContext>
            {/* Append: lo slot va in coda alle card. */}
            {previewIndex === bodyMissions.length && (
              <div className="vp-drop-slot" style={{ height: gapPx }} aria-hidden />
            )}
            {collapsed && hiddenCount > 0 && (
              <div className="text-muted small text-center pt-1">
                {t('operatorColumn.collapsedHidden', { count: hiddenCount })}
              </div>
            )}
          </>
        ) : collapsed ? (
          <div className="text-muted small text-center py-2" aria-hidden="true">
            {hiddenCount > 0
              ? t('operatorColumn.collapsedHidden', { count: hiddenCount })
              : t('operatorColumn.noneInProgress')}
          </div>
        ) : (
          // Operatore vuoto: la drop-zone cresce ad altezza-card quando ci passi
          // sopra, "preparandosi" ad accogliere la card in arrivo.
          <div className={`vp-op-empty ${previewIndex != null ? 'vp-op-empty--armed' : ''}`}
               style={previewIndex != null ? { minHeight: gapPx } : undefined}
               aria-hidden="true">
            <i className="bi bi-hand-index-thumb" />
            <span>{t('operatorColumn.emptyDrop')}</span>
          </div>
        )}
      </div>
    </Card>
  );
}

function SortableOpCard({ mission, isSelected, highlight, innerRef, onClick }:
  { mission: AssignedMission; isSelected: boolean; highlight?: boolean;
    innerRef?: React.MutableRefObject<HTMLDivElement | null>;
    onClick: (e: React.MouseEvent) => void; }) {
  const id = opCardId(mission.mission_id);
  // active = operator currently picking; done = mission complete. Neither
  // can be moved between operators or reordered, so skip the drag listeners
  // (clicks still work for select/inspect).
  const isDraggable = mission.status === 'pending' || mission.status === 'assigned';
  const sortable = useSortable({ id, disabled: !isDraggable });
  const dragProps = isDraggable
    ? { ...sortable.attributes, ...sortable.listeners }
    : {};
  const setRefs = (node: HTMLDivElement | null): void => {
    sortable.setNodeRef(node);
    if (innerRef) innerRef.current = node;
  };
  return (
    <div ref={setRefs} {...dragProps}
         className={highlight ? 'vp-card-flash' : undefined}
         style={{
           transform: CSS.Transform.toString(sortable.transform),
           transition: sortable.transition,
           // The active card is hidden — the floating <DragOverlay> is its proxy
           // (otherwise it would shift under sibling columns: the z-index issue).
           opacity: sortable.isDragging ? 0 : 1,
           cursor: isDraggable ? 'grab' : 'default',
           touchAction: 'none',
         }}>
      <AssignmentCard
        kind={mission.kind}
        location="op"
        pickList={mission.pick_list}
        claimOrder={mission.claim_order}
        shardIndex={mission.kind === 'whole_list' ? undefined : mission.shard_index}
        shardTotal={mission.kind === 'whole_list' ? undefined : mission.shard_total}
        qtyAssignedTotal={mission.qty_assigned_total}
        taskCount={mission.task_count}
        status={mission.status}
        isSelected={isSelected}
        onClick={onClick} />
    </div>
  );
}
